/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { BackupRecord, User } from '../types';
import { BackupRepository } from '../repositories/backupRepository';
import { AuditService } from './auditService';
import { resolveAuthoritativeContext } from '../security/tenantContext';
import { generateCorrelationId } from '../security/backendContracts';
import { auth } from '../utils/firebaseAuth';

export class BackupService {
  static async createBackup(
    tipo: 'AUTOMATICA (18:00)' | 'MANUAL',
    data: BackupRecord['data'],
    currentUser: User | null
  ): Promise<BackupRecord> {
    const authContext = resolveAuthoritativeContext(currentUser);
    const correlationId = generateCorrelationId('BCK-GEN');
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().split(' ')[0];
    const backupId = `BACKUP-${tipo.startsWith('AUTO') ? 'AUTO' : 'MANUAL'}-${dateStr.replace(/-/g, '')}-${Date.now().toString().slice(-4)}`;

    const backup = await BackupRepository.createBackupRecord(
      backupId,
      `${dateStr} ${timeStr}`,
      tipo,
      authContext.companyId,
      currentUser?.username || 'SYSTEM',
      data
    );

    await AuditService.logOperation(
      currentUser?.nombreCompleto || 'Sistema Automático',
      'Copias Seguridad',
      'Crear Copia',
      `Copia de seguridad ${tipo} generada con éxito (SHA-256: ${backup.hashSha256?.substring(0, 16)}..., ${data.ordenes.length} órdenes, ${data.disposiciones.length} disposiciones).`,
      authContext.companyId,
      undefined,
      correlationId
    );

    return backup;
  }

  static async validateAndPrepareRestore(
    backup: BackupRecord,
    currentUser: User | null
  ): Promise<{ valid: boolean; message: string; correlationId: string }> {
    const authContext = resolveAuthoritativeContext(currentUser);
    const correlationId = generateCorrelationId('BCK-RESTORE');

    if (!authContext.isDirector && !authContext.isSuperAdmin) {
      return {
        valid: false,
        message: 'Operación denegada. Solo el Director Técnico o el SuperAdministrador pueden autorizar restauraciones.',
        correlationId
      };
    }

    const targetTenant = backup.companyId || 'company-insumedic';
    if (!authContext.isSuperAdmin && targetTenant !== authContext.companyId) {
      return {
        valid: false,
        message: 'Operación denegada. No tiene autorización para restaurar datos pertenecientes a otro tenant.',
        correlationId
      };
    }

    const integrity = await BackupRepository.verifyIntegrity(backup);
    if (!integrity.valid) {
      return {
        valid: false,
        message: 'Error crítico de seguridad: El hash SHA-256 del resguardo no coincide con los datos.',
        correlationId
      };
    }

    return {
      valid: true,
      message: 'Validación de integridad SHA-256 y contexto de tenant exitosa.',
      correlationId
    };
  }

  static async dryRunServerRestore(
    backup: BackupRecord,
    currentUser: User | null
  ): Promise<{ success: boolean; data?: any; error?: string; errorCode?: string; correlationId: string }> {
    const correlationId = generateCorrelationId('DRYRUN-API');

    if (!auth.currentUser) {
      return {
        success: false,
        error: 'Sesión no autenticada en Firebase Auth. Inicie sesión para continuar.',
        errorCode: 'UNAUTHENTICATED',
        correlationId
      };
    }

    try {
      const idToken = await auth.currentUser.getIdToken();
      if (!idToken) {
        return {
          success: false,
          error: 'No se pudo obtener un token de identidad válido.',
          errorCode: 'UNAUTHENTICATED',
          correlationId
        };
      }

      const headers: Record<string, string> = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      };

      const response = await fetch('/api/restore-backup/dry-run', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          correlationId,
          clientTimestamp: new Date().toISOString(),
          backupId: backup.id,
          expectedSha256: backup.hashSha256 || '',
          reason: 'Verificación Dry-Run Pre-Restauración'
        })
      });

      let resJson: any;
      try {
        resJson = await response.json();
      } catch (jsonErr) {
        return {
          success: false,
          error: `Respuesta inválida del servidor (HTTP ${response.status})`,
          errorCode: 'INTERNAL_ERROR',
          correlationId
        };
      }

      if (!response.ok || !resJson.success) {
        return {
          success: false,
          error: resJson.errorMessage || 'Error en validación Server-Side Dry-Run',
          errorCode: resJson.errorCode || 'RESTORE_FAILED',
          correlationId: resJson.correlationId || correlationId
        };
      }

      return {
        success: true,
        data: resJson.data,
        correlationId: resJson.correlationId || correlationId
      };
    } catch (err: any) {
      return {
        success: false,
        error: `Error de red o servidor: ${err.message}`,
        errorCode: 'INTERNAL_ERROR',
        correlationId
      };
    }
  }

  static async executeServerRestore(
    backup: BackupRecord,
    currentUser: User | null,
    reason: string = 'Restauración autorizada por Director Técnico'
  ): Promise<{ success: boolean; data?: any; error?: string; errorCode?: string; correlationId: string }> {
    const correlationId = generateCorrelationId('RESTORE-EXEC');

    if (!auth.currentUser) {
      return {
        success: false,
        error: 'Sesión no autenticada en Firebase Auth. Inicie sesión para continuar.',
        errorCode: 'UNAUTHENTICATED',
        correlationId
      };
    }

    try {
      const idToken = await auth.currentUser.getIdToken();
      if (!idToken) {
        return {
          success: false,
          error: 'No se pudo obtener un token de identidad válido.',
          errorCode: 'UNAUTHENTICATED',
          correlationId
        };
      }

      const headers: Record<string, string> = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      };

      const response = await fetch('/api/restore-backup/execute', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          correlationId,
          clientTimestamp: new Date().toISOString(),
          backupId: backup.id,
          expectedSha256: backup.hashSha256 || '',
          reason
        })
      });

      let resJson: any;
      try {
        resJson = await response.json();
      } catch (jsonErr) {
        return {
          success: false,
          error: `Respuesta inválida del servidor (HTTP ${response.status})`,
          errorCode: 'INTERNAL_ERROR',
          correlationId
        };
      }

      if (!response.ok || !resJson.success) {
        return {
          success: false,
          error: resJson.errorMessage || 'Fallo en la ejecución server-side de restauración',
          errorCode: resJson.errorCode || 'RESTORE_FAILED',
          correlationId: resJson.correlationId || correlationId
        };
      }

      return {
        success: true,
        data: resJson.data,
        correlationId: resJson.correlationId || correlationId
      };
    } catch (err: any) {
      return {
        success: false,
        error: `Error de conexión con el backend: ${err.message}`,
        errorCode: 'INTERNAL_ERROR',
        correlationId
      };
    }
  }

  static async generateWormManifest(
    backupId: string,
    currentUser: User | null,
    retentionDays: number = 1825
  ): Promise<{ success: boolean; data?: any; error?: string }> {
    const authContext = resolveAuthoritativeContext(currentUser);

    try {
      const idToken = await auth.currentUser?.getIdToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }

      const response = await fetch('/api/worm-backup/export', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          backupId,
          retentionDays
        })
      });

      const resJson = await response.json();
      if (!response.ok || !resJson.success) {
        return {
          success: false,
          error: resJson.errorMessage || 'No se pudo generar el manifiesto WORM'
        };
      }

      return {
        success: true,
        data: resJson.data
      };
    } catch (err: any) {
      return {
        success: false,
        error: err.message
      };
    }
  }
}
